import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { v4 as uuid } from 'uuid';

import { Modal, Table, Form, Input, Switch, Flex, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';
import { LoadingOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';
import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception, error, securityRestriction } from '@extensions/notification';
import { delayAction } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { IMarking } from '@entities/marking';
import { IMarkingFilter } from '@entities/marking-filter';

import { Permission, hasPermission } from '@enums/permission';
import { unescape } from 'lodash';

interface IMarkingProps extends IBaseComponentProps {
    consigneeId: string | undefined;
    api: NotificationInstance;
    onRefill: (value: Array<IMarking>) => void;
}

const filterContext: string = 'Markings';

const Markings = (props: IMarkingProps) => {
    const { consigneeId, api, onRefill, serverFetch } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const [form] = Form.useForm();

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IMarkingFilter>((s) => s.filters[filterContext]);

    const [markings, setMarkings] = useState<Array<IMarking>>([]);
    const [currentMarking, setCurrentMarking] = useState<IMarking>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [markingOpen, setMarkingOpen] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [initFilter, setInitFilter] = useState<IMarkingFilter>();

    useEffect(() => {
        if (!consigneeId) {
            setSelectedIds([]);
            setMarkings([]);
            setCurrentMarking(undefined);
            setInitFilter(undefined);
        }

        if (filter?.consigneeId != consigneeId) {
            const newFilter: IMarkingFilter = { consigneeId: consigneeId, isArchived: false };
            setInitFilter(newFilter);
        }
    }, [consigneeId]);

    useEffect(() => {
        if (!refreshRequired || !consigneeId) return;

        let cleanup = false;
        const fetchData = async () => {
            setLoading(true);

            setSelectedIds([]);
            setMarkings([]);
            setCurrentMarking(undefined);

            var copyFilter = { ...filter };
            if (copyFilter.isArchived) {
                copyFilter.isArchived = undefined;
            }

            const promises = [
                await serverFetch('markings', { method: 'GET', queryParams: copyFilter })
                    .then((data: Array<IMarking>) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setMarkings(result[0][0] || []);
                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!initFilter) return;

        d(setFilter(initFilter, filterContext));
    }, [initFilter]);

    useEffect(() => {
        if (!filter) return;

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDelete = () => {
        if (!currentMarking) return;

        if (consigneeId) {
            serverFetch(`markings/${currentMarking?.id}`, { method: 'DELETE' })
                .then(() => {
                    onSelectChange([]);
                    setRefreshRequired(true);
                })
                .catch((ex: any) => {
                    exception(api, 'Ошибка удаления маркировки', ex, () => d(userLoaded(undefined)));
                });
        } else {
            var copyMarkings = [...markings];
            copyMarkings = copyMarkings.filter((m) => m.id != currentMarking.id);

            setMarkings(copyMarkings);
            onRefill(copyMarkings);
        }
    };

    const onSave = (entity: IMarking) => {
        if (!entity.id) {
            var marking = markings.find((m) => m.code == entity.code);
            if (marking) {
                error(api, 'Маркировка с указанным кодом уже добавлена');
                return;
            }
        }

        if (consigneeId) {
            setLoading(true);

            serverFetch(`markings`, { method: entity.id ? 'PUT' : 'POST', bodyData: entity })
                .then(() => {
                    setLoading(false);
                    setRefreshRequired(true);

                    form.resetFields();
                    setMarkingOpen(false);
                })
                .catch((ex: any) => {
                    setLoading(false);
                    exception(api, 'Ошибка сохранения маркировки', ex, () => d(userLoaded(undefined)));
                });
        } else {
            var copyMarkings = [...markings];

            if (entity.id) {
                marking = copyMarkings.find((m) => m.id == entity.id);
                if (marking) {
                    marking.code = entity.code;
                    marking.isArchived = entity.isArchived;
                }
            } else {
                entity.id = uuid();
                copyMarkings.push(entity);
            }

            form.resetFields();
            setMarkingOpen(false);

            setMarkings(copyMarkings);
            onRefill(copyMarkings);
        }
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageConsignees)) {
                                securityRestriction(api, [Permission.ManageConsignees]);
                                return;
                            }

                            setCurrentMarking(undefined);
                            setMarkingOpen(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentMarking,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageConsignees)) {
                                securityRestriction(api, [Permission.ManageConsignees]);
                                return;
                            }

                            setMarkingOpen(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentMarking,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageConsignees)) {
                                securityRestriction(api, [Permission.ManageConsignees]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить маркировку "${currentMarking?.code}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = markings.find((o) => o.id == selectedRowKeys[0]);
            setCurrentMarking(entity);
        } else {
            setCurrentMarking(undefined);
        }
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            key='сode'
                            placeholder='Код'
                            style={{ width: 230 }}
                            value={filter?.searchText}
                            onChange={(data) => {
                                d(setFilter({ ...filter, searchText: data.target.value }, filterContext));
                            }}
                        />,
                        <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isArchived}
                                onChange={(value) => {
                                    d(setFilter({ ...filter, isArchived: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IMarking> = [
            {
                title: 'Код',
                width: 600,
                onCell: (record) => ({
                    style: {
                        color: record.isArchived ? 'var(--main-red)' : '',
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex align='center' gap='small'>
                            <div>{record.code}</div>
                            {record.isArchived && (
                                <Tag variant='solid' color='red'>
                                    Добавлена в архив
                                </Tag>
                            )}
                        </Flex>
                    );
                },
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                showHeader={false}
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={markings}
                loading={tableLoading}
                onRow={(record: IMarking) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58) }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 260px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            <Modal
                destroyOnHidden={true}
                closable={false}
                title={currentMarking ? 'Изменить маркировку' : 'Добавить маркировку'}
                open={markingOpen}
                okText='ОК'
                onOk={() => form.submit()}
                onCancel={() => {
                    form.resetFields();
                    setMarkingOpen(false);
                }}
                width={500}
            >
                <Form
                    preserve={false}
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    style={{ marginTop: 20 }}
                    onFinish={onSave}
                    form={form}
                >
                    <Form.Item hidden name='id' initialValue={currentMarking?.id}>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='consigneeId' initialValue={consigneeId}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label='Код'
                        name='code'
                        required={true}
                        initialValue={currentMarking?.code}
                        rules={[{ required: true, message: 'Укажите код маркировки' }]}
                    >
                        <Input
                            autoFocus
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                        />
                    </Form.Item>
                    {currentMarking?.id && (
                        <Form.Item label='Архив' name='isArchived' initialValue={currentMarking?.isArchived} valuePropName='checked'>
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {modalContextHolder}
        </div>
    );
};

export default Markings;
